/**
 * /tools/identity-snapshot?clientId=X — F-028 (2026-05-24); reworked
 * 2026-06-13 (#6): each axis now accepts MULTIPLE identity statements,
 * added one at a time (the same repeater the Superhero "qualities" tool
 * uses), so the client can name more than one facet of who they are per
 * axis. Every non-empty entry becomes a high-confidence, axis-tagged
 * identity proposal in the Carpe Diem pool via
 * apps/api/src/virtues/sources/identity-snapshot.ts.
 *
 * Backward compatible: legacy single-string completions
 * (energyIdentity / workIdentity / loveIdentity) hydrate into a one-item
 * list. The Save button stays disabled until something changes
 * (F-007 — the button state IS the saved confirmation).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { ApiError } from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';
import { AxisIcon } from '@/components/axis-icon';

const MAX_PER_AXIS = 8;
const AXES = ['energy', 'work', 'love'] as const;
type Axis = (typeof AXES)[number];

type FormState = Record<Axis, string[]> & { reflection: string };

const defaultForm = (): FormState => ({
  energy: [''],
  work: [''],
  love: [''],
  reflection: '',
});

/** Read an axis list from the new array shape, falling back to the legacy single string. */
function hydrateAxis(raw: unknown, legacy: unknown): string[] {
  if (Array.isArray(raw)) {
    const list = raw.filter((x): x is string => typeof x === 'string');
    if (list.length > 0) return list;
  }
  if (typeof legacy === 'string' && legacy.trim().length > 0) return [legacy];
  return [''];
}

function trimmedEntries(list: string[]): string[] {
  return list.map((s) => s.trim()).filter((s) => s.length > 0);
}

export function IdentitySnapshotPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const { isPristine, markSaved } = usePristine<FormState>(defaultForm());

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'identity-snapshot');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = {
            energy: hydrateAxis(p.energyIdentities, p.energyIdentity),
            work: hydrateAxis(p.workIdentities, p.workIdentity),
            love: hydrateAxis(p.loveIdentities, p.loveIdentity),
            reflection: typeof p.reflection === 'string' ? p.reflection : '',
          };
          setForm(next);
          markSaved(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit = useMemo(
    () =>
      !submitting &&
      hydrated &&
      !isPristine(form) &&
      AXES.every((axis) => trimmedEntries(form[axis]).length >= 1),
    [submitting, hydrated, isPristine, form],
  );

  const updateEntry = (axis: Axis, idx: number, value: string): void => {
    setForm((prev) => {
      const next = [...prev[axis]];
      next[idx] = value;
      return { ...prev, [axis]: next };
    });
  };
  const addEntry = (axis: Axis): void => {
    setForm((prev) =>
      prev[axis].length >= MAX_PER_AXIS ? prev : { ...prev, [axis]: [...prev[axis], ''] },
    );
  };
  const removeEntry = (axis: Axis, idx: number): void => {
    setForm((prev) =>
      prev[axis].length <= 1 ? prev : { ...prev, [axis]: prev[axis].filter((_, i) => i !== idx) },
    );
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.identitySnapshot.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        energyIdentities: trimmedEntries(form.energy),
        workIdentities: trimmedEntries(form.work),
        loveIdentities: trimmedEntries(form.love),
        ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
      };
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'identity-snapshot',
        { payload },
      );
      markSaved(form);
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.identitySnapshot.errors.generic'));
      } else {
        setSubmitError(t('tools.identitySnapshot.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="identity-snapshot-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.identitySnapshot.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.identitySnapshot.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s7">
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.identitySnapshot.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.identitySnapshot.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.identitySnapshot.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s4"
        noValidate
      >
        {AXES.map((axis) => (
          <AxisRepeater
            key={axis}
            axis={axis}
            entries={form[axis]}
            onChange={(idx, v) => updateEntry(axis, idx, v)}
            onAdd={() => addEntry(axis)}
            onRemove={(idx) => removeEntry(axis, idx)}
          />
        ))}

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.identitySnapshot.reflection.label')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.identitySnapshot.optional')}
              </span>
            </span>
            <p className="text-xs text-gray-1 mb-s2">
              {t('tools.identitySnapshot.reflection.hint')}
            </p>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-20"
              data-testid="identity-snapshot-reflection"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="identity-snapshot-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="identity-snapshot-saved-at"
          >
            ✓ {t('tools.identitySnapshot.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting
            ? t('tools.identitySnapshot.submitting')
            : t('tools.identitySnapshot.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}

interface AxisRepeaterProps {
  axis: Axis;
  entries: string[];
  onChange: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

function AxisRepeater({ axis, entries, onChange, onAdd, onRemove }: AxisRepeaterProps): JSX.Element {
  const { t } = useTranslation();
  const axisLabel = t(`tools.identitySnapshot.${axis}.label`);
  return (
    <article
      className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s3"
      data-testid={`identity-snapshot-axis-${axis}`}
    >
      <header className="flex items-center gap-s2">
        <AxisIcon axis={axis} size={20} ariaLabel={axisLabel} />
        <span className="form-label mb-0">{axisLabel}</span>
      </header>
      <p className="text-sm text-gray-2 italic">{t(`tools.identitySnapshot.${axis}.prompt`)}</p>

      <div className="flex flex-col gap-s2">
        {entries.map((entry, idx) => (
          <div key={idx} className="flex gap-s2">
            <input
              type="text"
              value={entry}
              onChange={(e) => onChange(idx, e.target.value)}
              maxLength={280}
              placeholder={t(`tools.identitySnapshot.${axis}.placeholder`)}
              className="input-field flex-1"
              data-testid={`identity-snapshot-${axis}-${idx}`}
              aria-label={`${axisLabel} ${idx + 1}`}
            />
            {entries.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="text-sm text-gray-1 hover:text-flame-ember px-s2"
                data-testid={`identity-snapshot-${axis}-remove-${idx}`}
                aria-label={t('tools.identitySnapshot.removeLabel')}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {entries.length < MAX_PER_AXIS && (
          <button
            type="button"
            onClick={onAdd}
            className="text-sm text-flame-deep hover:text-flame self-start"
            data-testid={`identity-snapshot-${axis}-add`}
          >
            + {t('tools.identitySnapshot.addLabel')}
          </button>
        )}
      </div>
    </article>
  );
}
